import React, { useState, useEffect } from 'react';
import { Card, Timeline, Tag, List, Typography, Empty, Spin, Row, Col, Button } from 'antd';
import { CalendarOutlined, FolderOutlined, TagOutlined, PlusOutlined } from '@ant-design/icons';
import { Link } from 'react-router-dom';
import blogData from '../../utils/blogData';
import './index.css';

const { Title, Text } = Typography;

const Archive = () => {
  const [loading, setLoading] = useState(true);
  const [archives, setArchives] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({
    totalPosts: 0,
    totalViews: 0,
    totalLikes: 0,
    archiveCount: 0
  });

  useEffect(() => {
    loadArchiveData();
    
    // 监听数据更新事件
    window.addEventListener('blogDataUpdated', loadArchiveData);
    
    // 添加示例数据（如果没有文章）
    if (blogData.getPostCount() === 0) {
      blogData.addSampleData();
    }
    
    return () => {
      window.removeEventListener('blogDataUpdated', loadArchiveData);
    };
  }, []);

  const loadArchiveData = () => {
    setLoading(true);
    try {
      const archiveData = blogData.getArchives();
      const categoryData = blogData.getCategories();
      const statsData = blogData.getStats();
      
      setArchives(archiveData);
      setCategories(categoryData);
      setStats(statsData);
    } catch (error) {
      console.error('加载归档数据失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const monthNames = ['一月', '二月', '三月', '四月', '五月', '六月', 
                     '七月', '八月', '九月', '十月', '十一月', '十二月'];

  if (loading) {
    return (
      <div className="spin-container">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="archive-page">
      <div className="archive-header">
        <Title level={2} className="page-title">
          <CalendarOutlined /> 文章归档
        </Title>
        <Text type="secondary">共 {stats.totalPosts} 篇文章</Text>
        
        <div className="archive-actions" style={{ marginTop: 16 }}>
          <Button 
            type="primary" 
            icon={<PlusOutlined />}
            onClick={() => window.location.href = '/write'}
          >
            写新文章
          </Button>
        </div>
      </div>

      <Row gutter={[32, 32]}>
        {/* 统计数据 */}
        <Col xs={24}>
          <Card className="stats-card">
            <Row gutter={[16, 16]}>
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <div className="stat-number">{stats.totalPosts}</div>
                  <div className="stat-label">文章总数</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <div className="stat-number">{stats.archiveCount}</div>
                  <div className="stat-label">归档月份</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <div className="stat-number">{stats.totalViews}</div>
                  <div className="stat-label">阅读总量</div>
                </div>
              </Col>
              <Col xs={12} sm={6}>
                <div className="stat-item">
                  <div className="stat-number">{stats.totalLikes}</div>
                  <div className="stat-label">点赞总数</div>
                </div>
              </Col>
            </Row>
          </Card>
        </Col>

        {/* 时间线归档 */}
        <Col xs={24} lg={16}>
          <Card className="archive-section" title="时间线归档">
            {archives.length > 0 ? (
              <Timeline mode="alternate" className="archive-timeline">
                {archives.map((archive, index) => (
                  <Timeline.Item
                    key={`${archive.year}-${archive.month}`}
                    color={index % 2 === 0 ? 'blue' : 'green'}
                    dot={<CalendarOutlined />}
                  >
                    <div className="timeline-item">
                      <Title level={4} className="timeline-date">
                        {archive.year}年 {monthNames[archive.month - 1]}
                      </Title>
                      <div className="timeline-content">
                        <Tag color="processing" className="article-count">
                          {archive.count} 篇文章
                        </Tag>
                        <Link 
                          to={`/archive/${archive.year}/${archive.month}`}
                          className="view-link"
                        >
                          查看详情 →
                        </Link>
                      </div>
                    </div>
                  </Timeline.Item>
                ))}
              </Timeline>
            ) : (
              <Empty 
                description="暂无归档数据"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              >
                <Button 
                  type="primary"
                  onClick={() => window.location.href = '/write'}
                >
                  去写第一篇文章
                </Button>
              </Empty>
            )}
          </Card>
        </Col>

        {/* 分类统计 */}
        <Col xs={24} lg={8}>
          <Card className="archive-section" title={
            <span><FolderOutlined /> 分类统计</span>
          }>
            {categories.length > 0 ? (
              <List
                dataSource={categories}
                renderItem={(category) => (
                  <List.Item className="category-item">
                    <div className="category-info">
                      <Text strong>{category.name}</Text>
                      <Text type="secondary">共 {category.count} 篇文章</Text>
                    </div>
                    <div className="category-bar">
                      <div 
                        className="category-progress"
                        style={{ 
                          width: `${(category.count / Math.max(...categories.map(c => c.count)) || 1) * 100}%` 
                        }}
                      />
                    </div>
                  </List.Item>
                )}
              />
            ) : (
              <Empty 
                description="暂无分类"
                image={Empty.PRESENTED_IMAGE_SIMPLE}
              />
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};

export default Archive;